import { Bell, CalendarDays, LayoutDashboard, Library, ListChecks, UserCog, UserRound, Users, type LucideIcon } from 'lucide-react';
import type { Action, SessionDto } from '@orienta/shared';

export type Persona = SessionDto['persona'];

export interface NavItem {
  key: string;
  label: string;
  to: string;
  icon: LucideIcon;
  /** Personas que veem o item (ausente = todas). */
  personas?: Persona[];
  /** Exige ao menos uma destas ações institucionais (em alguma filiação, ou na instituição do contexto). */
  requires?: Action[];
  /** Somente superadministração da plataforma. */
  superAdminOnly?: boolean;
  /** Condição adicional sobre a sessão (ex.: orientação). */
  when?: (session: SessionDto) => boolean;
  /** Ignora espaços de orientação pessoais (não são instituições cadastradas). */
  institutionsOnly?: boolean;
  /** Rótulo alternativo por persona. */
  labelFor?: Partial<Record<Persona, string>>;
  section: 'principal' | 'gestao' | 'conta';
}

/**
 * Configuração de navegação. Novas áreas (tarefas, entregas, check-ins, agenda,
 * relatórios…) entram aqui com `personas`/`requires`.
 */
export const NAV_ITEMS: NavItem[] = [
  { key: 'dashboard', label: 'Painel', to: '/', icon: LayoutDashboard, section: 'principal', labelFor: { student: 'Meu painel' } },
  // Foco da ferramenta: orientador(a) gerenciando orientandos.
  { key: 'advisees', label: 'Orientandos', to: '/orientandos', icon: Users, section: 'principal', when: (s) => s.advising.canAdvise },
  // Agenda: central de controle das atividades de todos os orientandos, por data.
  { key: 'agenda', label: 'Agenda', to: '/agenda', icon: CalendarDays, section: 'principal', when: (s) => !s.user.isSuperAdmin || s.advising.canAdvise },
  { key: 'templates', label: 'Modelos de atividades', to: '/modelos', icon: Library, section: 'principal', when: (s) => s.advising.canAdvise },
  // Orientando(a): o próprio plano de atividades.
  { key: 'my-plan', label: 'Minhas atividades', to: '/minhas-atividades', icon: ListChecks, section: 'principal', when: (s) => s.advising.activeLinksAsStudent > 0 },
  // Trabalhos (/trabalhos) e gestão institucional (/instituicoes/:id) são do modelo institucional legado: rotas mantidas, fora do menu.
  { key: 'users', label: 'Usuários', to: '/admin/usuarios', icon: UserCog, section: 'gestao', superAdminOnly: true },
  // Superadministração edita os modelos da plataforma (quem também orienta já vê o item acima).
  { key: 'platform-templates', label: 'Modelos de atividades', to: '/modelos', icon: Library, section: 'gestao', superAdminOnly: true, when: (s) => !s.advising.canAdvise },
  { key: 'notifications', label: 'Notificações', to: '/notificacoes', icon: Bell, section: 'conta' },
  { key: 'profile', label: 'Perfil', to: '/perfil', icon: UserRound, section: 'conta' },
];

export interface ResolvedNavItem extends Omit<NavItem, 'to'> {
  to: string;
}

/** Itens visíveis para a sessão (e instituição em contexto). */
export function navItemsFor(session: SessionDto, contextInstitutionId: string | null): ResolvedNavItem[] {
  const out: ResolvedNavItem[] = [];
  for (const item of NAV_ITEMS) {
    if (item.personas && !item.personas.includes(session.persona)) continue;
    if (item.superAdminOnly && !session.user.isSuperAdmin) continue;
    if (item.when && !item.when(session)) continue;
    let to = item.to;
    if (item.requires) {
      const eligible = session.memberships.filter((m) => (!item.institutionsOnly || !m.isWorkspace) && item.requires!.some((a) => m.actions.includes(a)));
      if (!eligible.length) continue;
      if (to.includes(':institutionId')) {
        const target = eligible.find((m) => m.institutionId === contextInstitutionId) ?? eligible[0];
        if (!target) continue;
        to = to.replace(':institutionId', target.institutionId);
      }
    }
    out.push({ ...item, to, label: item.labelFor?.[session.persona] ?? item.label });
  }
  return out;
}
